import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Box } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";

import { ProfileCard } from "../../index";
import { useTooltipPosition } from "MVVM/Hooks/useTooltipPosition";

interface IProfileCardWrapperProps {
  children: React.ReactNode;
  username?: string;
  avatar?: string;
  isMatchHistory?: boolean;
  gameId?: string;
  displayName?: string;
  matchId?: string;
  disabled?: boolean;
  forceBelow?: boolean;
}

export const ProfileCardWrapper = ({
  children,
  username,
  avatar,
  isMatchHistory = false,
  gameId,
  displayName,
  matchId,
  disabled = false,
  forceBelow = false,
}: IProfileCardWrapperProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipPosition = useTooltipPosition({
    isVisible: isHovered,
    targetRef: wrapperRef,
    forceBelow,
  });

  const handleMouseEnter = () => {
    if (disabled) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const shouldShowProfileCard = username || isMatchHistory;

  return (
    <>
      <WrapperContainer
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </WrapperContainer>
      {shouldShowProfileCard &&
        isHovered &&
        createPortal(
          <ProfileCardTooltipPortal
            style={{
              left: tooltipPosition.left,
              top: tooltipPosition.top,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <ProfileCard
              username={username}
              avatar={avatar}
              isMatchHistory={isMatchHistory}
              gameId={gameId}
              displayName={displayName}
              matchId={matchId}
            />
          </ProfileCardTooltipPortal>,
          document.body,
        )}
    </>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(5px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const WrapperContainer = styled(Box)`
  display: inline-block;
  position: relative;
`;

const ProfileCardTooltipPortal = styled(Box)`
  animation: ${fadeIn} 0.2s ease-out;
  animation-fill-mode: both;
  pointer-events: auto;
  position: fixed;
  z-index: 1000;
`;
